import axios from 'axios';
import jwtDecode from 'jwt-decode';

const timeout = 30000;
const defaultErrorMessage = 'Something went wrong, please try again later.';

export default class Api {
    axios = null;
    router = null;
    token = '';
    userId = '';
    workspaceId = '';
    apiEndpoint = '';

    context = {
        status: '',
        search: {},
    };

    constructor(router) {
        this.axios = axios.create();
        this.router = router;
    }

    getUserId() {
        return this.userId;
    }

    contextQuery() {
        const r = this.router.currentRoute;
        if (!r) {
            return {};
        }
        const {from, to, incident} = r.query || {};
        return {from, to, incident};
    }

    setLastWorkspace(workspaceId) {
        localStorage.setItem('last-workspace', workspaceId);
    }

    getLastWorkspace() {
        return this.workspaceId || localStorage.getItem('last-workspace');
    }

    setLastProject(projectId) {
        if (!projectId) {
            return;
        }
        const v = localStorage.getItem('last-project');
        const lp = v ? JSON.parse(v) : {};
        lp[this.workspaceId] = projectId;
        localStorage.setItem('last-project', JSON.stringify(lp));
    }

    getLastProject() {
        const v = localStorage.getItem('last-project');
        if (v) {
            const lp = JSON.parse(v);
            return lp[this.workspaceId];
        }
        return null;
    }

    jwtDecode(s) {
        return jwtDecode(s);
    }

    checkToken(cb) {
        if (this.token) {
            try {
                const d = this.jwtDecode(this.token);
                if (d.exp && Date.now() < d.exp * 1000) {
                    cb(d.jti === '0');
                    return
                }
            } catch {
                //
            }
        }
        this.axios.post('/api/auth/token').then((response) => {
            this.token = response.data;
            try {
                const d = jwtDecode(this.token);
                this.userId = (d.jti === '0' ? '' : d.jti) || '';
                cb(d.jti === '0');
            } catch {
                this.userId = '';
                cb(true);
            }
        }).catch(() => {
            this.token = '';
            this.userId = '';
            cb(true);
        });
    }

    checkWorkspaceApi(workspaceId, cb) {
        if (this.workspaceId === workspaceId) {
            cb();
            return;
        }
        this.axios.get(`/api/workspace/${workspaceId}/api`).then((response) => {
            this.workspaceId = workspaceId;
            this.apiEndpoint = response.data.trim();
            this.setLastWorkspace(workspaceId);
            cb();
        }).catch((error) => {
            cb();
        });
    }

    getWorkspaceAuthMethod(workspaceId, cb) {
        this.axios.get(`/api/workspace/${workspaceId}/auth`).then((response) => {
            cb(response.data.trim());
        }).catch((error) => {
            cb();
        });
    }

    getWorkspaceSamlAuthenticationRequest(workspaceId, cb) {
        this.axios.get(`/api/auth/saml/${workspaceId}`).then((response) => {
            cb(response.data.trim());
        }).catch((error) => {
            cb();
        });
    }

    register(form, cb) {
        this.token = '';
        this.auth('/api/auth/register', 'POST', form, cb);
    }

    login(form, cb) {
        this.token = '';
        this.auth('/api/auth/login', 'POST', form, cb);
    }

    logout(cb){
        this.token = '';
        this.auth('/api/auth/logout', 'POST', null, cb);
    }

    authGoogle(form, cb) {
        this.auth('/api/auth/google', 'POST', form, cb);
    }

    account(cb) {
        this.private('/api/account/info', 'GET', null, cb);
    }

    organization(id, form, cb) {
        this.private(`/api/organization/${id}`, form ? 'POST' : 'GET', form, cb);
    }

    licenses(organizationId, form, cb) {
        this.private(`/api/organization/${organizationId}/licenses`, form ? 'POST' : 'GET', form, cb);
    }

    orgApiKeys(organizationId, form, cb) {
        this.private(`/api/organization/${organizationId}/api-keys`, form ? 'POST' : 'GET', form, cb);
    }

    accountSettings(form, cb) {
        this.private('/api/account/settings', form ? 'POST' : 'GET', form, cb);
    }

    setPassword(form, cb) {
        this.private('/api/account/password/set', 'POST', form, cb);
    }

    sendResetPasswordEmail(form, cb) {
        this.public('/api/account/password/reset/send', 'POST', form, cb);
    }

    resetPassword(token, cb) {
        this.public('/api/account/password/reset/perform', 'POST', JSON.stringify(token), cb);
    }

    sendActivationEmail(cb) {
        this.private('/api/account/activation/send', 'POST', null, cb);
    }

    activateAccount(token, cb) {
        this.private('/api/account/activation/confirm', 'POST', JSON.stringify(token), cb);
    }

    workspaceList(cb) {
        this.private('/api/workspace/list', 'GET', null, cb);
    }

    workspaceInfo(workspaceId, projectId, cb) {
        this.private(`/api/workspace/${workspaceId}/info`, 'GET', {project: projectId}, cb);
    }

    workspaceCreate(form, cb) {
        this.private('/api/workspace', 'POST', form, cb);
    }

    getProjects(workspaceId, cb) {
        this.private(`/api/workspace/${workspaceId}/projects`, 'GET', null, cb);
    }

    saveProject(workspaceId, projectId, form, cb) {
        if (!projectId) {
            this.private(`/api/workspace/${workspaceId}/project`, 'POST', form, cb);
        } else {
            this.private(`/api/workspace/${workspaceId}/project/${projectId}`, 'POST', form, cb);
        }
    }

    delProject(workspaceId, projectId, cb) {
        this.private(`/api/workspace/${workspaceId}/project/${projectId}`, 'DELETE', null, cb);
    }

    billing(workspaceId, form, cb) {
        this.private(`/api/workspace/${workspaceId}/billing`, form ? 'POST' : 'GET', form, cb);
    }

    users(workspaceId, form, cb) {
        this.private(`/api/workspace/${workspaceId}/users`, form ? 'POST' : 'GET', form, cb);
    }

    sso(workspaceId, form, cb) {
        this.private(`/api/workspace/${workspaceId}/sso`, 'POST', form, cb);
    }

    acceptInvitation(token, cb) {
        this.public(`/api/workspace/invitation/accept`, 'POST', JSON.stringify(token), cb);
    }

    workspacePath(subPath) {
        const workspaceId = this.router.currentRoute.params.workspaceId;
        return `/api/workspace/${workspaceId}/${subPath}`;
    }
    getIntegrations(cb) {
        this.private(this.workspacePath(`integrations`), 'GET', null, cb);
    }
    saveIntegration(integration, cb) {
        this.private(this.workspacePath(`integrations`), 'POST', integration, cb);
    }
    delIntegration(integration, cb) {
        this.private(this.workspacePath(`integrations`), 'DELETE', integration, cb);
    }
    testIntegration(integration, cb) {
        this.private(this.workspacePath(`integrations`), 'PUT', integration, cb);
    }

    getCategories(cb) {
        this.private(this.workspacePath(`categories`), 'GET', null, cb);
    }
    saveCategory(form, cb) {
        this.private(this.workspacePath(`categories`), 'POST', form, cb);
    }
    delCategory(form, cb) {
        this.private(this.workspacePath(`categories`), 'DELETE', form, cb);
    }

    getInspections(cb) {
        this.private(this.workspacePath(`inspections`), 'GET', null, cb);
    }
    saveInspection(form, cb) {
        this.private(this.workspacePath(`inspections`), 'POST', form, cb);
    }

    projectPath(subPath, projectId) {
        return `/api/project/${projectId || this.router.currentRoute.params.projectId}/${subPath}`;
    }

    getOverview(view, query, cb) {
        this.private(this.projectPath(`overview/${view}`), 'GET', { query }, cb);
    }

    getApplication(appId, cb) {
        this.private(this.projectPath(`app/${appId}`), 'GET', null, cb);
    }

    getNode(name, cb) {
        this.private(this.projectPath(`node/${name}`), 'GET', null, cb);
    }

    ceRequest(subPath, method, data, cb) {
        const _cb = (data, meta) => {
            cb(data, meta.error);
        }
        this.private(this.projectPath(subPath), method, data, _cb);
    }

    search(cb) {
        this.ceRequest(`search`, 'GET', null, cb);
    }

    getPrometheusCompleteConfiguration(projectId) {
        return {
            remote: {
                apiPrefix: this.projectPath('prom', projectId) + '/api/v1',
                fetchFn: (input, init) => {
                    if (!init) {
                        init = {};
                    }
                    if (!init.headers) {
                        init.headers = {};
                    }
                    init.headers['Authorization'] = this.token;
                    return fetch(input, init);
                }
            },
        }
    }

    getProfile(appId, query, cb) {
        this.ceRequest(`app/${appId}/profile`, 'GET', {query}, cb);
    }
    saveProfileSettings(appId, form, cb) {
        this.ceRequest(`app/${appId}/profile`, 'POST', form, cb);
    }

    getTracing(appId, trace, cb) {
        this.ceRequest(`app/${appId}/tracing`, 'GET', {trace}, cb);
    }
    saveTracingSettings(appId, form, cb) {
        this.ceRequest(`app/${appId}/tracing`, 'POST', form, cb);
    }
    getLogs(appId, query, cb) {
        this.ceRequest(`app/${appId}/logs`, 'GET', {query}, cb);
    }
    saveLogsSettings(appId, form, cb) {
        this.ceRequest(`app/${appId}/logs`, 'POST', form, cb);
    }

    getRCA(appId, rcaFrom, rcaTo, cb) {
        this.private(this.projectPath(`app/${appId}/rca`), 'GET', {rcaFrom, rcaTo}, cb);
    }

    contactUs(form, cb) {
        this.public('/api/contactUs', 'POST', form, cb);
    }

    auth(url, method, data, cb) {
        const cfg = {
            headers: {'Content-Type': 'application/json'},
            url, method, data, timeout,
        };
        this.request(cfg, cb);
    }

    public(url, method, data, cb) {
        let params = {};
        if (method === 'GET' && data) {
            params = {...data};
        }

        // TODO: don't send context params for api calls which doesn't understand it
        const q = this.router.currentRoute.query;
        params = {...params, as: q.as, from: q.from, to: q.to, outage: q.outage, incident: q.incident};

        const cfg = {
            baseURL: this.apiEndpoint,
            headers: {'Authorization': this.token, 'Content-Type': 'application/json'},
            url, method, params, data, timeout,
        };
        this.request(cfg, cb);
    }

    private(url, method, data, cb) {
        this.checkToken((anonymous) => {
            if (anonymous && method !== 'GET') {
                // TODO: redirect or refresh page
            }
            this.public(url, method, data, cb);
        });
    }

    request(cfg, cb) {
        this.axios.request(cfg).then((response) => {
            try {
                const meta = {code: response.status};
                if (response.data && response.data.meta) {
                    meta.error = response.data.meta.error || '';
                    this.context.status = response.data.meta.status || '';
                    this.context.search = response.data.meta.search || {};
                }
                const data = (response.data && response.data.data !== undefined) ? response.data.data : response.data;
                cb(data, meta);
            } catch (error) {
                console.error(error);
            }
        }).catch((error) => {
            const status = error.response ? error.response.status : 499;
            const data = error.response && error.response.data && error.response.data.trim() || defaultErrorMessage;
            cb(null, {code: status, error: data});
        });
    }
}
